import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material'
import { AlertCircleIcon, ChevronDownIcon } from '@untitled-theme/icons-react'

import { StyledTooltip } from '@/shared/components/StyledTooltip'

import {
  HEATING_BASELINE_ROW,
  HEATING_FUNDING,
  HEATING_FUNDING_COMPONENTS,
  HEATING_FUNDING_PERIODS,
  HEATING_INCOME_GROUPS,
  HEATING_MAX_ELIGIBLE_COSTS,
  HEATING_MAX_ELIGIBLE_EXPENSES,
} from '../../constants/funding-overview.constants'

import DeltaPill from './DeltaPill'
import { GRID_SX, SUB_LABEL_SX } from './HeatingFundingSection.styles'
import HeatpumpCalculatorCard from './HeatpumpCalculatorCard'
import RateBar from './RateBar'
import SeparatedBlock from './SeparatedBlock'

const HeatingFundingSection = () => {
  const {
    title,
    subtitle,
    strapline,
    maxEligibleCostsLabel,
    componentsLabel,
    eligibleMeasuresNote,
    incomeGroupsLabel,
    incomeGroupsHint,
    source,
  } = HEATING_FUNDING

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, fontSize: '12px' }}>{title}</Typography>
      <Typography sx={{ fontSize: '12px', fontWeight: 400, lineHeight: '18px', marginTop: '8px' }}>
        {subtitle}
      </Typography>
      <Typography sx={{ fontSize: '10px', color: '#6A6E78', marginTop: '2px' }}>
        {strapline}
      </Typography>
      <Box sx={{ borderBottom: '1px solid #E1E1E4', marginTop: '12px' }} />

      <Box sx={{ overflow: 'auto' }}>
        <Box sx={{ minWidth: '660px', paddingTop: '12px' }}>
          <SeparatedBlock>
            {/* Period headers */}
            <Box sx={GRID_SX}>
              <Box />
              {HEATING_FUNDING_PERIODS.map(period => (
                <Box key={period.label} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{period.label}</Typography>
                  {period.sublabel && (
                    <Typography sx={{ fontSize: '9px', color: '#6A6E78' }}>
                      {period.sublabel}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>

            {/* Max eligible costs band */}
            <Box
              sx={{
                ...GRID_SX,
                backgroundColor: '#F5F5F7',
                borderRadius: '8px',
                padding: '8px 12px',
                marginTop: '8px',
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                {maxEligibleCostsLabel}
              </Typography>
              {HEATING_MAX_ELIGIBLE_COSTS.map((cost, index) => (
                <Typography
                  key={HEATING_FUNDING_PERIODS[index].label}
                  sx={{ fontSize: '12px', fontWeight: 600, textAlign: 'center' }}
                >
                  {cost}
                </Typography>
              ))}
            </Box>
          </SeparatedBlock>

          {/* Fördersatz building blocks */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '10px',
              flexWrap: 'wrap',
              margin: '16px 12px 8px',
            }}
          >
            <Typography sx={SUB_LABEL_SX}>{componentsLabel}</Typography>
            <Typography sx={{ fontSize: '9px', color: '#9CA0A8' }}>
              {eligibleMeasuresNote}
            </Typography>
          </Box>
          <SeparatedBlock>
            {HEATING_FUNDING_COMPONENTS.map(component => (
              <Box key={component.name} sx={{ ...GRID_SX, padding: '8px 12px' }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                      {component.name}
                    </Typography>
                    {component.tooltip && (
                      <StyledTooltip title={component.tooltip} placement="bottom">
                        <Box component="span" sx={{ display: 'inline-flex', cursor: 'default' }}>
                          <AlertCircleIcon style={{ width: 13, height: 13, color: '#464A54' }} />
                        </Box>
                      </StyledTooltip>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: '9px', color: '#6A6E78' }}>
                    {component.note}
                  </Typography>
                </Box>
                {component.values.map((value, index) => (
                  <RateBar key={HEATING_FUNDING_PERIODS[index].label} value={value} />
                ))}
              </Box>
            ))}

            {/* Baseline: base rate + speed bonus, no income bonus */}
            <Box sx={{ ...GRID_SX, padding: '8px 12px' }}>
              <Box>
                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                  {HEATING_BASELINE_ROW.name}
                </Typography>
                <Typography sx={{ fontSize: '9px', color: '#6A6E78' }}>
                  {HEATING_BASELINE_ROW.note}
                </Typography>
              </Box>
              {HEATING_BASELINE_ROW.cells.map((cell, index) => (
                <Box key={HEATING_FUNDING_PERIODS[index].label} sx={{ alignSelf: 'start' }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                    {cell.amount}
                  </Typography>
                  <RateBar value={cell.rate} />
                  {cell.delta && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}>
                      <DeltaPill delta={cell.delta} />
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </SeparatedBlock>

          {/* Max Zuschuss per income group */}
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              '&:before': {
                display: 'none',
              },
              backgroundColor: 'transparent',
            }}
          >
            <AccordionSummary
              expandIcon={<ChevronDownIcon style={{ width: 16, height: 16, color: '#464A54' }} />}
              sx={{
                flexDirection: 'row-reverse',
                gap: '10px',
                padding: '0 12px',
                minHeight: 'auto',
                width: 'fit-content',
                margin: '8px 0 4px',
                '& .MuiAccordionSummary-content': {
                  margin: 0,
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '10px',
                  flexWrap: 'wrap',
                },
              }}
            >
              <Typography sx={SUB_LABEL_SX}>{incomeGroupsLabel}</Typography>
              <Typography sx={{ fontSize: '9px', color: '#9CA0A8' }}>{incomeGroupsHint}</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ padding: 0 }}>
              <SeparatedBlock>
                {HEATING_INCOME_GROUPS.map((group, groupIndex) => (
                  <Box
                    key={group.range}
                    sx={{
                      ...GRID_SX,
                      alignItems: 'center',
                      padding: '12px',
                      borderTop: groupIndex === 0 ? 'none' : '1px solid #E1E1E4',
                    }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        {group.range}
                      </Typography>
                      <Typography sx={{ fontSize: '9px', color: '#6A6E78' }}>
                        {group.withChild}
                      </Typography>
                      <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#5BC06B' }}>
                        {group.incomeBonus} Einkommensbonus
                      </Typography>
                    </Box>
                    {group.cells.map((cell, index) => (
                      <Box key={HEATING_FUNDING_PERIODS[index].label} sx={{ alignSelf: 'start' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>
                          {cell.amount}
                        </Typography>
                        <RateBar value={cell.rate} />
                        {cell.delta && (
                          <Box
                            sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '4px' }}
                          >
                            <DeltaPill delta={cell.delta} />
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </SeparatedBlock>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>

      <Typography sx={{ fontSize: '9px', color: '#6A6E78', lineHeight: '14px', marginTop: '12px' }}>
        {source}
      </Typography>

      {/* Eligible-expenses tiers per residential unit */}
      <Box sx={{ marginTop: '24px' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '12px' }}>
          {HEATING_MAX_ELIGIBLE_EXPENSES.title}
        </Typography>
        <Typography
          sx={{ fontSize: '12px', fontWeight: 400, lineHeight: '18px', marginTop: '8px' }}
        >
          {HEATING_MAX_ELIGIBLE_EXPENSES.subtitle}
        </Typography>
        <Box sx={{ borderBottom: '1px solid #E1E1E4', marginTop: '12px' }} />
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px 24px',
            marginTop: '12px',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              flex: '1 1 320px',
              minWidth: 0,
              backgroundColor: '#F5F5F7',
              borderRadius: '12px',
              padding: '12px 16px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '4px 0',
              }}
            >
              {HEATING_MAX_ELIGIBLE_EXPENSES.columns.map(column => (
                <Typography key={column} sx={{ fontSize: '12px', fontWeight: 600 }}>
                  {column}
                </Typography>
              ))}
            </Box>
            {HEATING_MAX_ELIGIBLE_EXPENSES.rows.map(row => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  padding: '4px 0',
                }}
              >
                <Typography sx={{ fontSize: '12px', fontWeight: 400 }}>{row.label}</Typography>
                <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>{row.amount}</Typography>
              </Box>
            ))}
            <Typography
              sx={{ fontSize: '9px', color: '#6A6E78', lineHeight: '14px', marginTop: '8px' }}
            >
              {HEATING_MAX_ELIGIBLE_EXPENSES.hint}
            </Typography>
          </Box>

          <Box sx={{ flex: '1 1 300px', minWidth: 0, display: 'flex' }}>
            <HeatpumpCalculatorCard />
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default HeatingFundingSection
